import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  GraduationCap, Radio, Users, ChevronDown, Globe, Menu, X,
  BookOpen, FlaskConical, Bot, Route, LayoutDashboard, Mic,
  Video, UserPlus, Mail, MessageSquare, Heart, Briefcase, Phone,
  LogOut, Sparkles, Shield,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { CoachingApi } from '../../services/api';
import '../../styles/header.css';

const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'it', label: 'Italiano', short: 'IT' },
];

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, isAdmin, isInstructor, logout, token } = useAuth();
  const [mentorApproved, setMentorApproved] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef(null);

  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileWorld, setMobileWorld] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setMentorApproved(false);
      return;
    }
    let cancelled = false;
    CoachingApi.getMyMentor(token)
      .then((res) => {
        if (!cancelled) setMentorApproved(res.data?.status === 'approved');
      })
      .catch(() => {
        if (!cancelled) setMentorApproved(false);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, token]);

  const isHome = location.pathname === '/';
  const lang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const portal = isAdmin
    ? { to: '/admin-dashboard', label: 'Command', icon: Shield }
    : isInstructor
      ? { to: '/creator-dashboard', label: 'Studio', icon: Sparkles }
      : { to: '/studentportal', label: t('nav.studentPortal'), icon: LayoutDashboard };

  const worlds = [
    {
      id: 'academy',
      label: t('nav.academy') || 'Academy',
      to: '/academy',
      accent: 'amber',
      icon: GraduationCap,
      kicker: 'Learn',
      blurb: 'Courses, labs, and AI-guided paths for working engineers.',
      links: [
        { to: '/courses', label: t('nav.courses'), icon: BookOpen, hint: 'Catalog' },
        { to: '/create-path', label: 'E-Learning Path', icon: Route, hint: 'AI' },
        { to: '/virtuallabs', label: t('nav.virtualLabs'), icon: FlaskConical, hint: 'Labs' },
        { to: '/aitutoring', label: t('nav.aiTutoring'), icon: Bot, hint: 'Tutor' },
        { to: '/studentportal', label: t('nav.studentPortal'), icon: LayoutDashboard, hint: 'You' },
      ],
    },
    {
      id: 'talks',
      label: 'QoreTalks',
      to: '/qoretalks',
      accent: 'rose',
      icon: Radio,
      kicker: 'Broadcast',
      blurb: 'Live rooms and long-form conversations with operators.',
      links: [
        { to: '/qoretalks', label: t('nav.episodes') || 'Episodes', icon: Mic, hint: 'Watch' },
        { to: '/qoretalks/live', label: t('nav.liveStream') || 'Live', icon: Video, hint: 'Live', live: true },
        { to: '/qoretalks/apply', label: t('nav.guests') || 'Guests', icon: UserPlus, hint: 'Speak' },
        { to: '/qoretalks/subscribe', label: t('nav.subscribe') || 'Subscribe', icon: Mail, hint: 'Alerts' },
      ],
    },
    {
      id: 'connect',
      label: t('nav.connect') || 'Connect',
      to: '/qoreconnect',
      accent: 'cyan',
      icon: Users,
      kicker: 'Network',
      blurb: 'Forum, mentors, and roles for the engineering community.',
      links: [
        { to: '/qoreconnect', label: t('nav.community') || 'Hub', icon: Users, hint: 'Hub' },
        { to: '/qoreconnect/forum', label: t('nav.forum'), icon: MessageSquare, hint: 'Talk' },
        { to: '/qoreconnect/mentorship', label: t('nav.mentorship'), icon: Heart, hint: 'Grow' },
        { to: '/qoreconnect/jobs', label: t('nav.jobs'), icon: Briefcase, hint: 'Work' },
      ],
    },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
    setMobileWorld(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpenMenu(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const displayName = user?.name?.split(' ')[0] || user?.firstName || 'You';
  const initials = (displayName || 'Q').slice(0, 1).toUpperCase();
  const activeWorld = worlds.find((w) => openMenu === w.id);

  const go = (path) => {
    navigate(path);
    setOpenMenu(null);
    setMobileOpen(false);
  };

  return (
    <div className="qn-nav-root" ref={rootRef}>
      <header
        className={`qn-bar ${scrolled || !isHome ? 'qn-bar--solid' : 'qn-bar--ghost'} ${mobileOpen ? 'qn-bar--sheet' : ''}`}
        onMouseLeave={() => { if (activeWorld) setOpenMenu(null); }}
      >
        <div className="qn-bar__inner">
          <Link to="/" className="qn-brand" aria-label="QoreNova home">
            <span className="qn-brand__mark">
              <img src="/images/logo/logo-qorenova.png" alt="" />
            </span>
            <span className="qn-brand__word">
              Qore<span>Nova</span>
            </span>
          </Link>

          <nav className="qn-pillars" aria-label="Primary">
            <NavLink to="/" end className={({ isActive }) => `qn-simple ${isActive ? 'is-on' : ''}`}>
              {t('nav.home')}
            </NavLink>
            <NavLink to="/about" className={({ isActive }) => `qn-simple ${isActive ? 'is-on' : ''}`}>
              {t('nav.about')}
            </NavLink>
            {worlds.map((world) => {
              const Icon = world.icon;
              const on = location.pathname.startsWith(world.to);
              return (
                <div key={world.id} className="qn-pillar">
                  <button
                    type="button"
                    className={`qn-pillar__btn qn-pillar__btn--${world.accent} ${on ? 'is-on' : ''} ${openMenu === world.id ? 'is-open' : ''}`}
                    aria-expanded={openMenu === world.id}
                    onMouseEnter={() => setOpenMenu(world.id)}
                    onFocus={() => setOpenMenu(world.id)}
                    onClick={() => setOpenMenu((m) => (m === world.id ? null : world.id))}
                  >
                    <Icon size={15} />
                    {world.label}
                    <ChevronDown size={13} className="qn-chevron" />
                  </button>
                </div>
              );
            })}
          </nav>

          <div className="qn-actions">
            <div className="qn-lang">
              <button
                type="button"
                className={`qn-icon-btn ${openMenu === 'lang' ? 'is-open' : ''}`}
                aria-label="Language"
                onClick={() => setOpenMenu((m) => (m === 'lang' ? null : 'lang'))}
              >
                <Globe size={16} />
                <span>{lang.short}</span>
              </button>
              {openMenu === 'lang' && (
                <div className="qn-pop qn-pop--lang">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      className={i18n.language === l.code ? 'is-on' : ''}
                      onClick={() => { i18n.changeLanguage(l.code); setOpenMenu(null); }}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link to="/contact" className="qn-ghost">
              <Phone size={14} />
              <span>{t('header.contact')}</span>
            </Link>

            {isAuthenticated ? (
              <div className="qn-user">
                <button
                  type="button"
                  className="qn-avatar-btn"
                  onClick={() => setOpenMenu((m) => (m === 'user' ? null : 'user'))}
                >
                  <span className="qn-avatar">{initials}</span>
                  <span className="qn-avatar-name">{displayName}</span>
                </button>
                {openMenu === 'user' && (
                  <div className="qn-pop qn-pop--user">
                    <div className="qn-pop__who">
                      <span className="qn-avatar qn-avatar--lg">{initials}</span>
                      <div>
                        <strong>{user?.name || displayName}</strong>
                        <small>{user?.email}</small>
                        <em>{user?.role || 'member'}</em>
                      </div>
                    </div>
                    <Link to={portal.to} className="qn-pop__row" onClick={() => setOpenMenu(null)}>
                      <portal.icon size={16} /> {portal.label}
                    </Link>
                    {mentorApproved && (
                      <Link to="/mentor-dashboard" className="qn-pop__row" onClick={() => setOpenMenu(null)}>
                        <Heart size={16} /> Mentor studio
                      </Link>
                    )}
                    <button
                      type="button"
                      className="qn-pop__row qn-pop__row--danger"
                      onClick={() => { logout(); setOpenMenu(null); navigate('/'); }}
                    >
                      <LogOut size={16} /> {t('nav.logout') || 'Log out'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button type="button" className="qn-cta" onClick={() => go('/login')}>
                {t('nav.login')}
              </button>
            )}

            <button
              type="button"
              className="qn-burger"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {activeWorld && (
          <div
            className={`qn-mega qn-mega--${activeWorld.accent}`}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <div className="qn-mega__intro">
              <span className="qn-mega__kicker">{activeWorld.kicker}</span>
              <h3>{activeWorld.label}</h3>
              <p>{activeWorld.blurb}</p>
              <Link to={activeWorld.to} className="qn-mega__enter" onClick={() => setOpenMenu(null)}>
                Enter {activeWorld.label}
              </Link>
            </div>
            <div className="qn-mega__grid">
              {activeWorld.links.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="qn-mega__card"
                    onClick={() => setOpenMenu(null)}
                  >
                    <span className="qn-mega__icon"><Icon size={18} /></span>
                    <span>
                      <strong>
                        {link.label}
                        {link.live && <i className="qn-live-dot" />}
                      </strong>
                      <small>{link.hint}</small>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </header>

      <div className={`qn-sheet ${mobileOpen ? 'is-open' : ''}`} aria-hidden={!mobileOpen}>
        <div className="qn-sheet__scroll">
          {isAuthenticated ? (
            <button type="button" className="qn-sheet__me" onClick={() => go(portal.to)}>
              <span className="qn-avatar">{initials}</span>
              <span>
                <strong>{displayName}</strong>
                <small>{portal.label}</small>
              </span>
            </button>
          ) : (
            <button type="button" className="qn-cta qn-cta--block" onClick={() => go('/login')}>
              {t('nav.login')}
            </button>
          )}

          <div className="qn-sheet__langs">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                type="button"
                className={i18n.language === l.code ? 'is-on' : ''}
                onClick={() => i18n.changeLanguage(l.code)}
              >
                {l.short}
              </button>
            ))}
          </div>

          <Link to="/" className="qn-sheet__link" onClick={() => setMobileOpen(false)}>{t('nav.home')}</Link>
          <Link to="/about" className="qn-sheet__link" onClick={() => setMobileOpen(false)}>{t('nav.about')}</Link>

          {worlds.map((world) => {
            const Icon = world.icon;
            const open = mobileWorld === world.id;
            return (
              <div key={world.id} className={`qn-sheet__world qn-sheet__world--${world.accent}`}>
                <button type="button" onClick={() => setMobileWorld(open ? null : world.id)}>
                  <Icon size={16} /> {world.label} <ChevronDown size={14} />
                </button>
                {open && (
                  <div>
                    {world.links.map((link) => (
                      <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}>
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <Link to="/contact" className="qn-sheet__link" onClick={() => setMobileOpen(false)}>
            {t('header.contact')}
          </Link>

          {isAuthenticated && (
            <button
              type="button"
              className="qn-sheet__link qn-sheet__link--danger"
              onClick={() => { logout(); navigate('/'); }}
            >
              {t('nav.logout') || 'Log out'}
            </button>
          )}
        </div>
      </div>
      {mobileOpen && <button type="button" className="qn-veil" aria-label="Close menu" onClick={() => setMobileOpen(false)} />}
    </div>
  );
};

export default Header;
